/**
 * What the map's "Alarms" filter offers.
 *
 * The map reports every alarm under its top-level category, so the picker
 * offers top-level categories only — a child selection could never match what a
 * site carries. Within that limit it reads like the site grid's Alarms filter:
 * the same translated category names, the same catalogue ordering, a count of
 * how many sites carry one, and nothing offered that no site has. A category
 * already chosen stays offered even at a count of zero, so a narrowing arriving
 * from a shared link is always reachable to undo.
 *
 * Contains:
 * - countSitesByAlarmCategory — category key -> how many sites carry an alarm in it
 * - buildAlarmCategoryOptions — the picker's options, worst-first by catalogue order
 *
 * Returns plain JS — no JSX, no redux.
 */

/**
 * Count the sites carrying at least one alarm in each top-level category. A
 * site with three power alarms counts once, matching what the site grid's
 * Alarms filter reports.
 *
 * @param {object[]} [sites] - map site rows, each with an optional `alarms`
 *   array whose entries carry `category_key`.
 * @returns {Object<string, number>} category key -> site count.
 */
export function countSitesByAlarmCategory(sites) {
  const counts = {};
  (sites ?? []).forEach(site => {
    const keys = new Set();
    (site?.alarms ?? []).forEach(alarm => {
      if (alarm?.category_key) keys.add(alarm.category_key);
    });
    keys.forEach(key => {
      counts[key] = (counts[key] ?? 0) + 1;
    });
  });
  return counts;
}

/**
 * Build the alarm-category options for the map's filter chip.
 *
 * @param {object[]} [categories] - the AlarmCategory catalogue; top-level rows
 *   are the ones with no `parent_id`.
 * @param {Object<string, number>} [counts] - category key -> site count.
 * @param {(localeKey: string, fallback: string) => string} translate - resolves
 *   a category's own `locale_key` (the alarm-namespace translator the grids use).
 * @param {string[]} [selectedKeys] - keys to keep offered whatever their count.
 * @returns {{ value: string, label: string }[]} options in catalogue order.
 */
export function buildAlarmCategoryOptions(categories, counts, translate, selectedKeys = []) {
  const kept = new Set(selectedKeys);
  return (categories ?? [])
    .filter(category => category?.parent_id == null && category?.key)
    .map(category => ({ category, count: counts?.[category.key] ?? 0 }))
    .filter(({ category, count }) => count > 0 || kept.has(category.key))
    .sort((a, b) => (a.category.display_order ?? 0) - (b.category.display_order ?? 0))
    .map(({ category, count }) => ({
      value: category.key,
      label: `(${count}) ${translate(category.locale_key || `alarms.cat_${category.key}`, category.display_name)}`,
    }));
}
